import { useState } from 'react';
import { regionOptions, yearOptions } from '@/mocks/site';

export type SortValue = 'latest' | 'hot';

interface FilterBarProps {
  region: string;
  year: string;
  sort: SortValue;
  onRegionChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onSortChange: (value: SortValue) => void;
}

const sortOptions: { value: SortValue; label: string }[] = [
  { value: 'latest', label: '最新' },
  { value: 'hot', label: '热门' },
];

function ChipGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="shrink-0 pt-1.5 text-xs text-foreground-500 w-10">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
              value === option
                ? 'bg-primary-500 text-background-950 font-semibold'
                : 'bg-background-200/60 text-foreground-700 hover:bg-background-300/60'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({
  region,
  year,
  sort,
  onRegionChange,
  onYearChange,
  onSortChange,
}: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const reset = () => {
    onRegionChange('全部');
    onYearChange('全部');
    onSortChange('latest');
  };

  return (
    <>
      {/* 桌面端筛选栏 */}
      <div className="hidden md:flex flex-col gap-4 rounded-lg bg-background-100 border border-background-200/70 p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-sm font-bold text-foreground-950">筛选条件</h3>
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置
          </button>
        </div>
        <ChipGroup label="地区" options={regionOptions} value={region} onChange={onRegionChange} />
        <ChipGroup label="年份" options={yearOptions} value={year} onChange={onYearChange} />
        <div className="flex items-start gap-3">
          <span className="shrink-0 pt-1.5 text-xs text-foreground-500 w-10">排序</span>
          <div className="flex gap-2">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onSortChange(option.value)}
                className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
                  sort === option.value
                    ? 'bg-primary-500 text-background-950 font-semibold'
                    : 'bg-background-200/60 text-foreground-700 hover:bg-background-300/60'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 移动端筛选触发 */}
      <div className="md:hidden flex items-center gap-2">
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="flex-1 flex items-center justify-center gap-2 h-11 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line text-lg" />
          筛选 · {region} · {year === '全部' ? '全部年份' : year}
        </button>
        <button
          type="button"
          onClick={reset}
          className="w-11 h-11 flex items-center justify-center rounded-md bg-background-100 border border-background-200/70 text-foreground-600 cursor-pointer"
          aria-label="重置筛选"
        >
          <i className="ri-refresh-line text-lg" />
        </button>
      </div>

      {sheetOpen && (
        <div className="md:hidden fixed inset-0 z-[70] flex items-end">
          <div
            className="absolute inset-0 bg-background-950/70"
            onClick={() => setSheetOpen(false)}
            role="presentation"
          />
          <div className="relative w-full max-h-[80vh] overflow-y-auto rounded-t-xl bg-background-100 p-5 pb-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-600 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="flex flex-col gap-5">
              <ChipGroup label="地区" options={regionOptions} value={region} onChange={onRegionChange} />
              <ChipGroup label="年份" options={yearOptions} value={year} onChange={onYearChange} />
              <div className="flex items-start gap-3">
                <span className="shrink-0 pt-1.5 text-xs text-foreground-500 w-10">排序</span>
                <div className="flex gap-2">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onSortChange(option.value)}
                      className={`px-3 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
                        sort === option.value
                          ? 'bg-primary-500 text-background-950 font-semibold'
                          : 'bg-background-200/60 text-foreground-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              className="mt-7 w-full h-11 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </>
  );
}